/**
 * TOSS Web Application — the top bar's icon control
 *
 * The one size an icon in a top bar has, on either surface: 40 × 40 with a 24 px glyph on a
 * phone, 36 × 36 with 20 px above 767. Both numbers come from the portal's bell, which is the
 * pair the owner approved; the internal header's own buttons were `IconButton` (44 × 44, glyph
 * 24) and read as the loudest thing on a bar whose other type is 12 px.
 *
 * The breakpoint is CSS, not JavaScript. A hook here would mean one `resize` subscription per
 * button on a bar that has five of them — the portal spent a wave removing exactly that (27
 * subscriptions, replaced by `useClientViewport`).
 *
 * **The badge is a number on both surfaces** (owner, 26.08.2026). The internal bell drew a dot:
 * a dispatcher could see that something had arrived but not whether it was one approval or
 * forty, while the messenger button two icons to the left had been printing the number all
 * along.
 *
 * A control that navigates takes `to` and renders a `<Link>`; everything else is a `<button>`.
 * The ref reaches the button only — the anchor branch is the portal's phone bell, which anchors
 * no popover (it opens a screen).
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface TopBarActionProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** The glyph. A component, so this layer decides its size rather than trusting the caller. */
  icon: LucideIcon;
  /** Accessible name, and the tooltip unless `title` says otherwise. */
  label: string;
  /** Unread count. Absent, zero or negative draws nothing; above 99 draws «99+». */
  badge?: number;
  /** Lit state — the panel this control opens is open, or its screen is the current one. */
  active?: boolean;
  /** Render a link to this route instead of a button. */
  to?: string;
  /** `aria-current="page"` on the link form. */
  current?: boolean;
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

// 48 on a phone, 36 above 767. The phone number is the WCAG 2.5.5 target the portal's bell
// already had (`w-10 h-10 touch-target` resolved to 48 through the utility's `min-width`);
// spelling it as a width keeps the desktop size from having to out-specify that utility.
const BOX =
  'relative flex flex-shrink-0 items-center justify-center rounded-lg transition-colors ' +
  'w-12 h-12 lg:w-9 lg:h-9 ' +
  'hover:bg-[var(--color-bg-secondary)]';

const GLYPH = 'w-6 h-6 lg:w-5 lg:h-5';

const BADGE =
  'absolute top-1 right-1 flex items-center justify-center rounded-full ' +
  'bg-[var(--color-error)] font-medium leading-none text-[var(--color-text-on-dark)] ' +
  'h-4 min-w-4 px-1 text-3xs lg:h-3.5 lg:min-w-3.5 lg:text-3xs';

// ============================================================================
// Component
// ============================================================================

export const TopBarAction = forwardRef<HTMLButtonElement, TopBarActionProps>(
  (
    { icon: Icon, label, badge, active = false, to, current = false, className = '', ...props },
    ref
  ) => {
    const classes = [
      BOX,
      active
        ? 'text-[var(--color-primary-600)]'
        : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const content = (
      <>
        <Icon className={GLYPH} aria-hidden="true" />
        {badge !== undefined && badge > 0 && (
          <span className={BADGE} aria-hidden="true">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </>
    );

    if (to) {
      return (
        <Link
          to={to}
          aria-label={label}
          aria-current={current ? 'page' : undefined}
          className={classes}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={props.title ?? label}
        className={classes}
        {...props}
      >
        {content}
      </button>
    );
  }
);

TopBarAction.displayName = 'TopBarAction';

// ============================================================================
// Group
// ============================================================================

export interface TopBarActionGroupProps {
  children?: ReactNode;
  className?: string;
}

/**
 * The tools, as one object on the bar.
 *
 * The internal surface has four controls in the right corner and will grow more; a flat row of
 * them reads as a pile the moment there are five. Pulled together by proximity, the tools are one
 * thing and the bell — the only control that reports a state rather than opening a utility —
 * stays outside the group, last (owner's choice, variant B, 26.08.2026).
 *
 * **The tint is gone (owner, 27.08.2026: «эту подложку в админке убрать, теперь не очень
 * смотрится»).** It was `--color-bg-secondary` — a grey pill — and it earned its keep while the
 * bar was a strip the colour of the canvas: the pill was the only thing telling the tools apart
 * from the field behind them. On the white card the bar became that day, the same pill is a grey
 * patch in the corner of a clean surface. Grouping now rests on spacing alone: the tools sit at
 * `gap-0.5` while `TopBarDivider` and the bell keep their own distance.
 *
 * Renders nothing when it is empty, so a surface with no tools (the portal) simply does not draw
 * a container.
 */
export function TopBarActionGroup({
  children,
  className = '',
}: TopBarActionGroupProps): JSX.Element | null {
  const isEmpty =
    children === null ||
    children === undefined ||
    children === false ||
    (Array.isArray(children) && children.every((child) => !child));

  if (isEmpty) return null;

  return (
    <div
      className={`flex items-center gap-0.5 ${className}`}
    >
      {children}
    </div>
  );
}

export default TopBarAction;
